import type { HandleClientError } from "@sveltejs/kit";
import { reportError, warmReporting } from "$lib/observability/report";
import {
  friendlyMessage,
  shouldReport,
  shouldReportWindowFailure,
} from "$lib/observability/report-policy";

// The browser half of the app's failure handling. It mirrors the server half
// deliberately: the same policy decides what is worth recording, the same
// module records it, and the same shape reaches the error page — so a visitor
// sees one kind of failure page whichever side produced it, and one identifier
// covers both.

// Read the reporting configuration once the page has gone quiet, so a failure
// after that can show the visitor an identifier immediately. Deliberately not
// on the hydration path: reading it there costs a network round trip before the
// page is interactive, on every page, to prepare for something that usually
// never happens.
if (typeof requestIdleCallback === "function") {
  requestIdleCallback(() => void warmReporting());
} else {
  setTimeout(() => void warmReporting(), 2_000);
}

// The request that served this document, stamped on the root element by the
// server hook. A prerendered page has none — it was built, not requested — and
// that absence is honest rather than a gap to fill with an invented value.
function pageRequestId(): string | null {
  return document.documentElement.dataset.requestId ?? null;
}

export const handleError: HandleClientError = ({ error, event, status }) => {
  const request_id = pageRequestId();
  if (!shouldReport(status, error)) {
    return { message: friendlyMessage(status), event_id: null, request_id };
  }

  const event_id =
    reportError(error, {
      requestId: request_id,
      route: event.route.id,
      status,
      component: "web-browser",
    }) ?? null;

  console.error(`[${request_id ?? "prerendered"}] ${status} on ${event.url.pathname}`, error);

  return { message: friendlyMessage(status), event_id, request_id };
};

// Everything the framework does not see. `handleError` covers a failure inside
// navigation and loading; a throw from a timer, an event handler or a
// background promise lands on `window` instead, and without these two listeners
// it would be visible only in a console nobody is reading. They are owned here
// rather than left to the SDK's own integration so that each one carries the
// page's request id, and so that the pair behaves the same whether or not a DSN
// is configured.
window.addEventListener("error", (failure: ErrorEvent) => {
  // What counts as worth recording is decided in one place, so the two
  // listeners here and the framework hook above cannot drift apart.
  if (!shouldReportWindowFailure(failure.error)) return;
  reportError(failure.error, {
    requestId: pageRequestId(),
    component: "web-browser-window",
  });
});

window.addEventListener("unhandledrejection", (failure: PromiseRejectionEvent) => {
  const reason: unknown = failure.reason;
  if (!shouldReportWindowFailure(reason)) return;
  reportError(reason, {
    requestId: pageRequestId(),
    component: "web-browser-window",
  });
});
